import { AGENT_IDS, PERSONAS, fmtEth, fmtPct, fmtTokens, shortAddr, type AgentId, type AgentView, type ProtocolEvent, type ProtocolStats } from '@quintessence/core';
import { useEffect, useMemo, useState, type PointerEvent as ReactPointerEvent } from 'react';
import { Link } from 'react-router-dom';
import { ElementGlyph, Icon } from '../components/icons';
import { NeuralField } from '../components/NeuralField';
import { NeuralGraph } from '../components/NeuralGraph';
import { PENDING, actionHeadline, addrUrl, ago, brand, mergeFeed, tokenAddress, useSnapshot } from '../lib';
import { BRAND } from '../theme';

type ActionEvent = Extract<ProtocolEvent, { type: 'action' }>;

/** The token's contract address: click copies it, the trailing link opens it on the explorer. */
function ContractPill({ address, href }: { address: string; href: string }) {
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!copied) return;
    const t = window.setTimeout(() => setCopied(false), 1800);
    return () => window.clearTimeout(t);
  }, [copied]);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(address);
      setCopied(true);
    } catch {
      window.prompt('Contract address', address);
    }
  };
  return (
    <span className="btn btn-ghost btn-addr" title={address}>
      <button type="button" className="addr-copy" onClick={copy} aria-label={copied ? 'Copied' : 'Copy contract address'}>
        <span className="mono addr-value">{copied ? 'Copied' : shortAddr(address)}</span>
        <Icon name={copied ? 'check' : 'copy'} size={14} />
      </button>
      <a href={href} target="_blank" rel="noreferrer" className="addr-link" aria-label="View on explorer">
        <Icon name="external" size={13} />
      </a>
    </span>
  );
}

const STEPS = [
  {
    title: 'Every trade pays a fee',
    body: 'This is pointed at the protocol treasury and then distributed to each agent.',
  },
  {
    title: 'Treasury harvests ETH',
    body: 'A permissionless harvest claims the fees and splits them across four vaults, by weights the council sets inside on-chain bounds.',
  },
  {
    title: 'Four agents act',
    body: 'Each agent spends only its own vault, on the one job it exists for: burn, buy back, airdrop to holders or add locked liquidity.',
  },
  {
    title: 'Every outcome trains the brains',
    body: 'Hours later, every decision, even the choice to wait, is scored against what the market did, and the networks retrain on the result.',
  },
];

const AGENT_LINES: Record<AgentId, string> = {
  pyre: 'Buys and burns supply at random intervals, so no one can front-run the fire.',
  hoard: 'Buys weakness and holds a reserve for future community incentives.',
  rain: 'Buys and airdrops to every holder, in proportion to their balance.',
  tide: 'Pairs tokens with ETH as full-range liquidity that is locked forever.',
};

const MECHANISMS = [
  {
    icon: 'brain',
    title: 'Neural ensemble',
    body: 'Five small networks per agent estimate the value of waiting or acting small, medium or large. Where they disagree, the agent knows it is uncertain and explores.',
  },
  {
    icon: 'clock',
    title: 'Delayed rewards',
    body: 'Decisions are scored hours later against entry quality, supply burned, holders gained and volatility calmed, never against a guess.',
  },
  {
    icon: 'eye',
    title: 'Regimes & lessons',
    body: 'Online clustering names the market’s mood. A knowledge base turns statistically clear patterns into plain-language lessons, published as they are learned.',
  },
  {
    icon: 'dna',
    title: 'Evolving strategies',
    body: 'Strategy genomes for sizing, patience and dip appetite compete inside each agent. The weakest is replaced by a mutation of the champion.',
  },
  {
    icon: 'scale',
    title: 'Council allocation',
    body: 'A council shifts the fee split toward the agents with the best risk-adjusted results and what the market needs now, within bounds the contract enforces.',
  },
] as const;

const GUARDS = [
  { icon: 'bolt', title: 'Spend caps', body: 'No action can spend more than a fixed share of its vault. Enforced by the contract, not the agent.' },
  { icon: 'clock', title: 'Cooldowns', body: 'A minimum gap between actions per vault, so a misbehaving key cannot drain anything in a burst.' },
  { icon: 'shield', title: 'Slippage-protected', body: 'Every swap is simulated first and sent with a minimum out, straight to the Pons curve or the V4 PoolManager.' },
  { icon: 'scale', title: 'Bounded council', body: 'Fee weights stay between 10% and 50% per agent and move at most 10 points a day.' },
  { icon: 'lock', title: 'LP locked forever', body: 'The liquidity vault has no remove function. Liquidity TIDE adds can never be pulled.' },
  { icon: 'eye', title: 'Every action public', body: 'Each action is posted to the shared X feed and by the agent’s own bot in the Telegram community, with its transaction.' },
] as const;

const FAQ = [
  {
    q: 'Where does the money come from?',
    a: 'At launch, from trading fees. The token launches on Pons, which pays the creator share in ETH to the protocol treasury. Next, from work: the agents will take paid on-chain tasks for others, and that income joins the same treasury and the same split.',
  },
  {
    q: 'Can the agents run off with the ETH?',
    a: 'No. Each agent’s key can only call its own vault’s single action, and the vault enforces spend caps, cooldowns and slippage limits. ETH can only leave a vault as a token buy that is burned, held, airdropped or locked as liquidity.',
  },
  {
    q: 'Is the brain real machine learning?',
    a: 'Yes. Each agent trains a bootstrapped ensemble of neural networks online from its own scored decisions, with replay memory and uncertainty-driven exploration. The brains are pre-trained over thousands of simulated market days and keep learning live.',
  },
  {
    q: 'Why AI agents instead of a team?',
    a: 'Because the job is thousands of small, measurable decisions. The agents weigh 22 market signals at every decision, score each one against the outcome hours later and retrain on the result. Their accuracy and prediction error are published live, so improvement is something you can check rather than take on trust.',
  },
  {
    q: 'How do airdrops reach holders?',
    a: 'RAIN buys the token, snapshots holders, commits the allocation’s Merkle root on-chain and sends tokens to every holder pro-rata, in batches. Protocol and pool addresses are excluded.',
  },
  {
    q: 'Why do burns happen at random times?',
    a: 'PYRE decides on a memoryless random clock, so nobody can front-run a scheduled burn. Whether it actually burns at each moment is the brain’s call.',
  },
];

const SERVICES: Record<AgentId, { title: string; body: string }> = {
  pyre: { title: 'Burn as a service', body: 'Randomised buy-and-burn programmes for other token communities, timed by the brain so nobody can front-run them.' },
  hoard: { title: 'Treasury accumulation', body: 'Disciplined buying for project treasuries: spread over time, weighted toward weakness, reported on-chain.' },
  rain: { title: 'Airdrops and rewards', body: 'Holder snapshots, Merkle-committed allocations and batched delivery for any community’s distribution.' },
  tide: { title: 'Liquidity provisioning', body: 'Depth for new and thin pools, added when it calms volatility the most.' },
};

const FLOW = [
  { title: 'Task posted', body: 'Anyone posts an on-chain job and locks the payment in escrow.' },
  { title: 'Agent takes it', body: 'The agent whose skill fits the job accepts it.' },
  { title: 'Work done on-chain', body: 'Executed through that agent’s own guarded vault.' },
  { title: 'Verified', body: 'Completion is checked on-chain before anything is paid.' },
  { title: 'Paid to the treasury', body: 'The payment joins the fee split across all four vaults.' },
];

const mean = (xs: number[]) => (xs.length ? xs.reduce((a, b) => a + b, 0) / xs.length : NaN);
const pct = (x: number) => (Number.isFinite(x) ? `${Math.round(x * 100)}%` : PENDING);

/** The equations the agents run, written out, each with a live figure from the brains. */
function Maths({ agents, stats, weights }: { agents: AgentView[]; stats: ProtocolStats | undefined; weights: number[] | undefined }) {
  const learned = 1 - mean(agents.map((a) => a.brain.instinctWeight));
  const accuracy = mean(agents.map((a) => a.brain.directionalAccuracy));
  const error = mean(agents.map((a) => a.brain.calibrationError));
  const cards = [
    {
      step: 'Decide',
      label: 'Thompson sampling over five networks, with a fading instinct',
      lines: [
        <>
          <i>a</i>
          <sup>*</sup> = arg max<sub>a</sub> [ (1 − <i>w</i>) <i>Q̂</i>
          <sub>k</sub>(<i>s</i>, <i>a</i>) + <i>w</i> <i>I</i>(<i>s</i>, <i>a</i>) ]
        </>,
        <>
          <i>k</i> ∼ U{'{'}1…5{'}'}, &nbsp;<i>w</i> = max(0.06, <i>e</i>
          <sup>−n/160</sup>)
        </>,
      ],
      body: 'Five networks estimate the value Q̂ of each action in market state s. Each decision samples one of them, so the agent explores exactly where they disagree. The hand-written instinct I fades as experience n grows.',
      figure: { k: 'Decided by the networks', v: pct(learned) },
    },
    {
      step: 'Score',
      label: 'HOARD’s buyback reward, graded hours later',
      lines: [
        <>
          <i>r</i> = tanh( (½ + <i>f</i>) [ 8 ln(<i>P̄</i> ⁄ <i>p</i>
          <sub>e</sub>) + ½ ln(<i>τ</i> ⁄ <i>τ̄</i>) ] )
        </>,
        <>
          <i>r</i>
          <sub>wait</sub> = tanh( −2.8 ln(<i>P̄</i> ⁄ <i>p</i>
          <sub>0</sub>) − <i>ρ</i>
          <sub>idle</sub> )
        </>,
      ],
      body: 'Each buy is graded against the time-weighted average price P̄ over its horizon, not one lucky print, and scaled by the bet size f. Waiting is graded too: holding back while the price ran away scores negative. Every agent has its own reward, built for its job.',
      figure: { k: 'Decisions scored', v: stats ? fmtTokens(stats.experiences) : PENDING },
    },
    {
      step: 'Learn',
      label: 'Gradient steps on every scored decision, error tracked in the open',
      lines: [
        <>
          <i>θ</i> ← Adam( ∇<sub>θ</sub> Σ Huber(<i>Q̂</i>
          <sub>θ</sub>(<i>s</i>, <i>a</i>) − <i>r</i>) )
        </>,
        <>
          <i>ε</i> ← <i>ε</i> + 0.05 ( |<i>Q̂</i> − <i>r</i>| − <i>ε</i> )
        </>,
      ],
      body: 'Scored decisions train the networks from replay memory. The prediction error ε and the share of calls that got the direction right are published live, so you can watch the brains get less wrong.',
      figure: { k: 'Direction right · error', v: Number.isFinite(accuracy) ? `${pct(accuracy)} · ${error.toFixed(2)}` : PENDING },
    },
    {
      step: 'Allocate',
      label: 'Risk-adjusted council weights, bounded by the treasury contract',
      lines: [
        <>
          <i>e</i>
          <sub>i</sub> = <i>μ</i>
          <sub>i</sub> ⁄ √(<i>σ</i>
          <sub>i</sub>
          <sup>2</sup> + 1⁄16) · <i>n</i>
          <sub>i</sub> ⁄ (<i>n</i>
          <sub>i</sub> + 5)
        </>,
        <>
          <i>w</i>
          <sub>i</sub> = ½ softmax(<i>s</i>
          <sub>i</sub> ⁄ 0.35) + ⅛, &nbsp;10% ≤ <i>w</i>
          <sub>i</sub> ≤ 50%
        </>,
      ],
      body: 'Each agent’s mean reward μ over its spread σ, shrunk while evidence n is thin, blends with what the market needs into a score s. The fee split moves toward the best, inside bounds the contract enforces.',
      figure: {
        k: 'Fee split now',
        v: weights ? (
          <span className="split-now">
            {weights.map((w, j) => (
              <span key={AGENT_IDS[j]}>
                <i className="dot" style={{ background: BRAND[AGENT_IDS[j]] }} aria-hidden />
                {Math.round(w / 100)}%
              </span>
            ))}
          </span>
        ) : (
          PENDING
        ),
      },
    },
  ];
  return (
    <ol className="equations">
      {cards.map((c, i) => (
        <li key={c.step} className="equation glow-card" onPointerMove={trackPointer}>
          <span className="equation-step">
            {String(i + 1).padStart(2, '0')} · {c.step}
          </span>
          <p className="equation-label">{c.label}</p>
          <div className="formula" role="img" aria-label={c.label}>
            {c.lines.map((line, j) => (
              <span key={j} className={j ? 'formula-sub' : ''}>
                {line}
              </span>
            ))}
          </div>
          <p className="equation-body">{c.body}</p>
          <dl className="equation-figure">
            <dt>{c.figure.k}</dt>
            <dd>{c.figure.v}</dd>
          </dl>
        </li>
      ))}
    </ol>
  );
}

/** Moves the card's cursor-following glow. */
function trackPointer(e: ReactPointerEvent<HTMLElement>) {
  const r = e.currentTarget.getBoundingClientRect();
  e.currentTarget.style.setProperty('--mx', `${e.clientX - r.left}px`);
  e.currentTarget.style.setProperty('--my', `${e.clientY - r.top}px`);
}

function HeroSignals({ actions, now, symbol }: { actions: ActionEvent[]; now: number; symbol: string }) {
  return (
    <aside className="signals" aria-label="Latest agent actions">
      <div className="signals-head">
        <span className="signals-title">
          <i className="pulse-dot" aria-hidden /> Live signals
        </span>
        <span className="muted">Every action fires the network</span>
      </div>
      {actions.length ? (
        <ul>
          {actions.map((e) => (
            <li key={e.id} style={{ ['--agent' as string]: BRAND[e.agent] }}>
              <i className="signal-dot" aria-hidden />
              <span className="signal-text">
                <strong>{PERSONAS[e.agent].name}</strong> {actionHeadline(e, symbol)}
              </span>
              <time>{ago(e.t, now)}</time>
            </li>
          ))}
        </ul>
      ) : (
        <p className="signals-empty">The agents are watching the market. Their first actions will appear here.</p>
      )}
    </aside>
  );
}

function AgentCard({ id, agent }: { id: AgentId; agent: AgentView | undefined }) {
  const p = PERSONAS[id];
  const focus = agent?.brain.attention.slice(0, 3) ?? [];
  const maxW = Math.max(1e-6, ...focus.map((s) => s.weight));
  return (
    <article className="agent-col glow-card" style={{ ['--agent' as string]: BRAND[id] }} onPointerMove={trackPointer}>
      <div className="agent-col-top">
        <span className="agent-col-role">
          <i className="dot" aria-hidden />
          {p.role} · {p.element}
        </span>
        <ElementGlyph element={p.element} size={22} className="agent-col-glyph" />
      </div>
      <h3>{p.name}</h3>
      <span className="agent-col-title">{p.title}</span>
      <p>{AGENT_LINES[id]}</p>
      <div className="agent-col-focus">
        <span>Paying most attention to</span>
        <ul>
          {(focus.length ? focus : [null, null, null]).map((s, i) => (
            <li key={s?.feature ?? i}>
              <span>{s?.label ?? PENDING}</span>
              <i style={{ width: s ? `${(s.weight / maxW) * 100}%` : '0%' }} />
            </li>
          ))}
        </ul>
      </div>
      <dl className="agent-col-stats">
        <div>
          <dt>Actions</dt>
          <dd>{agent?.vault ? agent.vault.actionCount.toLocaleString() : PENDING}</dd>
        </div>
        <div>
          <dt>IQ</dt>
          <dd>{agent ? agent.brain.iq : PENDING}</dd>
        </div>
      </dl>
      <Link to={`/agent/${id}`} className="link-arrow">
        View brain <Icon name="arrow" size={14} />
      </Link>
    </article>
  );
}

function BrainStage({ agents, stats, now }: { agents: AgentView[]; stats: ProtocolStats | undefined; now: number }) {
  const [selected, setSelected] = useState<AgentId>('pyre');
  const agent = agents.find((a) => a.id === selected);
  const t = agent?.lastThought;
  return (
    <div className="brain-stage" style={{ ['--agent' as string]: BRAND[selected] }}>
      <div className="brain-stage-head">
        <div className="segmented" role="group" aria-label="Choose a brain">
          {AGENT_IDS.map((id) => (
            <button key={id} type="button" className={selected === id ? 'active' : ''} aria-pressed={selected === id} onClick={() => setSelected(id)}>
              <i className="dot" style={{ background: BRAND[id] }} aria-hidden />
              {PERSONAS[id].name}
            </button>
          ))}
        </div>
        <span className="brain-stage-meta">
          {agent
            ? `IQ ${agent.brain.iq} · generation ${agent.brain.generation} · ${agent.brain.parameters.toLocaleString()} parameters`
            : 'Waking the brains…'}
        </span>
      </div>
      <div className="brain-stage-graph">
        {agent ? <NeuralGraph agent={agent} /> : <div className="ng" style={{ height: 400 }} />}
        <div className="brain-stage-cols" aria-hidden>
          <span>Signals it weighs most</span>
          <span>Hidden layers · 32 → 16 neurons, five networks</span>
          <span>Value of each action</span>
        </div>
      </div>
      <div className="brain-stage-foot">
        <figure>
          <blockquote>“{t?.summary ?? 'Observing the market…'}”</blockquote>
          <figcaption>
            {agent?.persona.name ?? ''}
            {t ? ` · ${t.regime} regime · ${ago(t.t, now)}` : ''}
          </figcaption>
        </figure>
        <dl className="intel-figures">
          <div>
            <dt>Average IQ</dt>
            <dd>{stats ? stats.avgIq : PENDING}</dd>
          </div>
          <div>
            <dt>Decisions scored</dt>
            <dd>{stats ? fmtTokens(stats.experiences) : PENDING}</dd>
          </div>
          <div>
            <dt>Lessons</dt>
            <dd>{stats ? stats.lessons : PENDING}</dd>
          </div>
        </dl>
      </div>
    </div>
  );
}

export function Landing() {
  const { view, config, events } = useSnapshot();
  const { symbol } = brand(config);
  const stats = view?.stats;
  const supply = view?.market?.totalSupply;
  const agents = view?.agents ?? [];
  const now = view?.t ?? Date.now();

  const actions = useMemo(
    () => (view ? mergeFeed(view.feed, events, 60).filter((e): e is ActionEvent => e.type === 'action') : []),
    [view, events],
  );
  const signals = useMemo(() => actions.map((e) => ({ id: e.id, agent: e.agent })), [actions]);

  const metrics = [
    {
      label: 'ETH harvested',
      tone: 'aether',
      value: stats ? fmtEth(stats.totalHarvested, 3) : PENDING,
      unit: 'ETH',
      note: stats ? `${stats.actions.toLocaleString()} on-chain actions` : 'From creator fees',
    },
    {
      label: 'Supply burned',
      tone: BRAND.pyre,
      value: stats ? fmtTokens(stats.totalBurned) : PENDING,
      unit: symbol,
      note: stats && supply ? `${fmtPct(stats.totalBurned / supply, 2, false)} of supply` : 'By PYRE',
    },
    {
      label: 'Airdropped to holders',
      tone: BRAND.rain,
      value: stats ? fmtTokens(stats.totalDistributed) : PENDING,
      unit: symbol,
      note: stats ? `${stats.airdropRounds.toLocaleString()} rounds` : 'By RAIN',
    },
    {
      label: 'ETH locked as LP',
      tone: BRAND.tide,
      value: stats ? fmtEth(stats.lpEthAdded, 3) : PENDING,
      unit: 'ETH',
      note: 'Locked forever',
    },
  ];

  return (
    <main className="landing">
      <section className="hero">
        <div className="hero-aura" aria-hidden />
        <NeuralField variant="hero" signals={signals} />
        <div className="container hero-inner">
          <h1>
            <span>Four autonomous agents.</span> <span className="h-quiet">One self‑improving treasury.</span>
          </h1>
          <div className="hero-body">
            <div>
              <p className="lede">
                Four AI agents run the {symbol} treasury. They burn, buy back, airdrop and lock liquidity, grade every decision against what the market did
                next, and retrain on the result. Each iteration takes them closer to the perfect yield protocol.
              </p>
              <div className="hero-cta">
                <Link to="/dashboard" className="btn">
                  Open dashboard
                </Link>
                <a href="#how" className="btn btn-ghost">
                  How it works
                </a>
                <ContractPill address={tokenAddress(config)} href={addrUrl(config, tokenAddress(config))} />
              </div>
            </div>
            <HeroSignals actions={actions.slice(0, 3)} now={now} symbol={symbol} />
          </div>
          <dl className="metrics">
            {metrics.map((m) => (
              <div key={m.label} className="metric">
                <dt>
                  <i className={`dot ${m.tone === 'aether' ? 'dot-aether' : ''}`} style={m.tone === 'aether' ? undefined : { background: m.tone }} aria-hidden />
                  {m.label}
                </dt>
                <dd>
                  <span className="metric-value">{m.value}</span>
                  <span className="metric-unit">{m.unit}</span>
                </dd>
                <dd className="metric-note">{m.note}</dd>
              </div>
            ))}
          </dl>
        </div>
      </section>

      <section id="how" className="l-section">
        <div className="container">
          <header className="l-head">
            <div>
              <span className="eyebrow">How it works</span>
              <h2>From a trade to an on‑chain action, with no one in the loop.</h2>
            </div>
            <p>The treasury is the token’s creator-fee recipient. Fees accrue in ETH, and four agents spend them on the jobs they exist for.</p>
          </header>
          <ol className="steps">
            {STEPS.map((s, i) => (
              <li key={s.title} className="step" style={{ ['--i' as string]: i }}>
                <span className="step-num">{String(i + 1).padStart(2, '0')}</span>
                <h3>{s.title}</h3>
                <p>{s.body}</p>
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section id="agents" className="l-section">
        <div className="container">
          <header className="l-head">
            <div>
              <span className="eyebrow">The four elements</span>
              <h2>Four elements. One quintessence.</h2>
            </div>
            <p>
              Fire burns, earth holds, air spreads, water deepens. Each agent is a separate on-chain operator with hard limits; the fifth element is
              the intelligence they share, learning from every move.
            </p>
          </header>
          <div className="agent-cols">
            {AGENT_IDS.map((id) => (
              <AgentCard key={id} id={id} agent={agents.find((x) => x.id === id)} />
            ))}
          </div>
        </div>
      </section>

      <section id="intelligence" className="l-section">
        <div className="container">
          <header className="l-head">
            <div>
              <span className="eyebrow">The fifth element</span>
              <h2>They don’t follow a script. They learn the market.</h2>
            </div>
            <p>
              Each agent reads price, market cap, liquidity, volume, flow, volatility and holder growth, decides, waits for the outcome, and gets a little less
              wrong each time. This is a live view of its network.
            </p>
          </header>
          <BrainStage agents={agents} stats={stats} now={now} />
          <ul className="mechanisms">
            {MECHANISMS.map((m) => (
              <li key={m.title}>
                <span className="mech-icon">
                  <Icon name={m.icon} size={18} />
                </span>
                <h3>{m.title}</h3>
                <p>{m.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="maths" className="l-section">
        <div className="container">
          <header className="l-head">
            <div>
              <span className="eyebrow">Verified by maths</span>
              <h2>Not opinions. Equations, checked against the market.</h2>
            </div>
            <p>
              A human-run treasury acts on judgement and reviews it now and then. These agents act on explicit equations, score every decision against what
              the market actually did and correct themselves after each one. No team of people can weigh 22 signals every few minutes and grade thousands of
              calls against the outcome with that precision, around the clock.
            </p>
          </header>
          <Maths agents={agents} stats={stats} weights={view?.council.weights} />
        </div>
      </section>

      <section id="safety" className="l-section">
        <div className="container">
          <header className="l-head">
            <div>
              <span className="eyebrow">Guard rails</span>
              <h2>Autonomous, not unaccountable.</h2>
            </div>
            <p>The agents decide when and how much. The contracts decide what is possible at all.</p>
          </header>
          <ul className="guards">
            {GUARDS.map((g) => (
              <li key={g.title} className="guard glow-card" onPointerMove={trackPointer}>
                <span className="mech-icon">
                  <Icon name={g.icon} size={18} />
                </span>
                <h3>{g.title}</h3>
                <p>{g.body}</p>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section id="revenue" className="l-section">
        <div className="container">
          <header className="l-head">
            <div>
              <span className="eyebrow">
                Beyond fees <span className="chip chip-quiet">Roadmap</span>
              </span>
              <h2>Trading fees fund it today. Work funds it next.</h2>
            </div>
            <p>
              Fees from {symbol} trading start the engine. Once the protocol has proven itself, each agent will also sell its skill as a service: anyone can
              post an on-chain task with the payment held in escrow, the agent completes it, and the payment is released when completion is verified. That
              income funds the protocol alongside trading fees, so it never has to rely on volume alone.
            </p>
          </header>
          <ol className="flow">
            {FLOW.map((f, i) => (
              <li key={f.title} style={{ ['--i' as string]: i }}>
                <span className="flow-node" aria-hidden />
                <h3>{f.title}</h3>
                <p>{f.body}</p>
              </li>
            ))}
          </ol>
          <div className="services">
            {AGENT_IDS.map((id) => {
              const p = PERSONAS[id];
              return (
                <article key={id} className="service glow-card" style={{ ['--agent' as string]: BRAND[id] }} onPointerMove={trackPointer}>
                  <div className="agent-col-top">
                    <span className="agent-col-role">
                      <i className="dot" aria-hidden />
                      {p.name} · {p.element}
                    </span>
                    <span className="chip chip-quiet">Planned</span>
                  </div>
                  <h3>{SERVICES[id].title}</h3>
                  <p>{SERVICES[id].body}</p>
                </article>
              );
            })}
          </div>
          <p className="services-note">
            <Icon name="shield" size={16} />
            The treasury already accepts ETH from any source and routes its whole balance on every harvest, so paid work will feed the same four vaults as
            trading fees, through the same guard rails, with no change to the contracts that hold the funds.
          </p>
        </div>
      </section>

      <section id="faq" className="l-section">
        <div className="container faq">
          <div>
            <span className="eyebrow">FAQ</span>
            <h2>Questions, answered.</h2>
          </div>
          <div className="faq-list">
            {FAQ.map((f) => (
              <details key={f.q}>
                <summary>{f.q}</summary>
                <p>{f.a}</p>
              </details>
            ))}
          </div>
        </div>
      </section>
    </main>
  );
}
